import supabase from './supabase';

/**
 * Service for managing portfolio items in the VA Sprouts application
 */
export const portfolioService = {
  /**
   * Fetches portfolio items for a specific user
   * @param {string} userId - The user ID to fetch portfolio items for
   * @param {Object} options - Additional options for the query
   * @returns {Promise<{data: Array, error: Object}>} - The query result
   */
  async getPortfolioItems(userId, options = {}) {
    try {
      const { featured, limit, sortBy, category } = options;
      
      let query = supabase
        .from('portfolio_items')
        .select('*')
        .eq('user_id', userId);
      
      // Apply featured filter if specified
      if (featured === true) {
        query = query.eq('is_featured', true);
      }
      
      // Apply category filter if specified
      if (category && category !== 'all') {
        query = query.eq('category', category);
      }
      
      // Apply sorting
      switch (sortBy) {
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        case 'oldest':
          query = query.order('created_at', { ascending: true });
          break;
        case 'featured':
          query = query.order('is_featured', { ascending: false }).order('created_at', { ascending: false });
          break;
        default:
          query = query.order('is_featured', { ascending: false }).order('created_at', { ascending: false });
      }
      
      // Apply limit if specified
      if (limit && Number.isInteger(limit)) {
        query = query.limit(limit);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Map field names for compatibility with PortfolioGallery component
      const mappedData = data.map(item => ({
        ...item,
        // Ensure these fields are available for PortfolioGallery component
        project_type: item.project_type || item.type,
        thumbnail: item.thumbnail || item.image_url,
        live_url: item.live_url || item.project_url,
        source_url: item.source_url || item.github_url
      }));
      
      return { data: mappedData, error: null };
    } catch (error) {
      console.error('Error in getPortfolioItems:', error);
      return { data: null, error };
    }
  },
  
  /**
   * Creates a new portfolio item
   * @param {Object} portfolioItem - The portfolio item data
   * @param {string} userId - The user ID that owns this portfolio item
   * @returns {Promise<{data: Object, error: Object}>} - The creation result
   */
  async createPortfolioItem(portfolioItem, userId) {
    try {
      // Map component field names to database field names
      const itemData = {
        user_id: userId,
        title: portfolioItem.title,
        description: portfolioItem.description,
        full_description: portfolioItem.full_description,
        category: portfolioItem.category,
        type: portfolioItem.project_type,
        project_type: portfolioItem.project_type,
        image_url: portfolioItem.thumbnail,
        thumbnail: portfolioItem.thumbnail,
        client: portfolioItem.client,
        completed_date: portfolioItem.completed_date,
        start_date: portfolioItem.start_date,
        end_date: portfolioItem.end_date,
        duration: portfolioItem.duration,
        client_rating: portfolioItem.client_rating,
        technologies: portfolioItem.technologies,
        project_url: portfolioItem.live_url,
        live_url: portfolioItem.live_url,
        github_url: portfolioItem.source_url,
        source_url: portfolioItem.source_url,
        is_featured: portfolioItem.is_featured || false,
        is_public: portfolioItem.is_public || true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Handle images field as JSONB array
      if (portfolioItem.images) {
        if (Array.isArray(portfolioItem.images) && typeof portfolioItem.images[0] === 'string') {
          // If it's already an array of strings
          itemData.images = portfolioItem.images;
        } else {
          // If it needs to be formatted as JSON
          itemData.images = JSON.stringify(portfolioItem.images || []);
        }
      }
      
      const { data, error } = await supabase
        .from('portfolio_items')
        .insert(itemData)
        .select();
        
      if (error) throw error;
      
      return { data: data[0], error: null };
    } catch (error) {
      console.error('Error in createPortfolioItem:', error);
      return { data: null, error };
    }
  },
  
  /**
   * Updates an existing portfolio item
   * @param {string} id - The portfolio item ID
   * @param {Object} portfolioItem - The updated portfolio item data
   * @param {string} userId - The user ID that owns this portfolio item (for security)
   * @returns {Promise<{data: Object, error: Object}>} - The update result
   */
  async updatePortfolioItem(id, portfolioItem, userId) {
    try {
      // Map component field names to database field names
      const itemData = {
        title: portfolioItem.title,
        description: portfolioItem.description,
        full_description: portfolioItem.full_description,
        category: portfolioItem.category,
        type: portfolioItem.project_type,
        project_type: portfolioItem.project_type,
        image_url: portfolioItem.thumbnail,
        thumbnail: portfolioItem.thumbnail,
        client: portfolioItem.client,
        completed_date: portfolioItem.completed_date,
        start_date: portfolioItem.start_date,
        end_date: portfolioItem.end_date,
        duration: portfolioItem.duration,
        client_rating: portfolioItem.client_rating,
        technologies: portfolioItem.technologies,
        project_url: portfolioItem.live_url,
        live_url: portfolioItem.live_url,
        github_url: portfolioItem.source_url,
        source_url: portfolioItem.source_url,
        is_featured: portfolioItem.is_featured,
        is_public: portfolioItem.is_public,
        updated_at: new Date().toISOString()
      };
      
      // Handle images field as JSONB array
      if (portfolioItem.images) {
        if (Array.isArray(portfolioItem.images) && typeof portfolioItem.images[0] === 'string') {
          // If it's already an array of strings
          itemData.images = portfolioItem.images;
        } else {
          // If it needs to be formatted as JSON
          itemData.images = JSON.stringify(portfolioItem.images || []);
        }
      }
      
      const { data, error } = await supabase
        .from('portfolio_items')
        .update(itemData)
        .eq('id', id)
        .eq('user_id', userId) // Security check to ensure only owner can update
        .select();
        
      if (error) throw error;
      return { data: data[0], error: null };
    } catch (error) {
      console.error('Error in updatePortfolioItem:', error);
      return { data: null, error };
    }
  },
  
  /**
   * Deletes a portfolio item
   * @param {string} id - The portfolio item ID
   * @param {string} userId - The user ID that owns this portfolio item (for security)
   * @returns {Promise<{success: boolean, error: Object}>} - The deletion result
   */
  async deletePortfolioItem(id, userId) {
    try {
      const { error } = await supabase
        .from('portfolio_items')
        .delete()
        .eq('id', id)
        .eq('user_id', userId); // Security check to ensure only owner can delete
        
      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error in deletePortfolioItem:', error);
      return { success: false, error };
    }
  },
  
  /**
   * Toggles the featured status of a portfolio item
   * @param {string} id - The portfolio item ID
   * @param {boolean} isFeatured - The new featured status
   * @param {string} userId - The user ID that owns this portfolio item (for security)
   * @returns {Promise<{data: Object, error: Object}>} - The update result
   */
  async toggleFeatured(id, isFeatured, userId) {
    try {
      const { data, error } = await supabase
        .from('portfolio_items')
        .update({ 
          is_featured: isFeatured,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', userId) // Security check to ensure only owner can update
        .select();
        
      if (error) throw error;
      return { data: data[0], error: null };
    } catch (error) {
      console.error('Error in toggleFeatured:', error);
      return { data: null, error };
    }
  }
};

export default portfolioService;
